<!--
  The PPA name is the card's one real link: its ::after stretches over the whole card (see
  .ppa-card-link in the CSS), so the whole card opens the details without nesting the other
  buttons inside a clickable element. Those buttons sit above it with `relative z-10`.
-->
<mat-card *ngIf="layout !== 'row'; else row" class="ppa-card relative h-full overflow-hidden" [class.ppa-card-list]="layout === 'list'">
  <div class="ppa-media">
    <!-- Above the stretched link (z-10), so a swipe doesn't open the details - though tapping a photo does -->
    <app-photo-strip
      *ngIf="ppa.Media?.length; else typeIcon"
      class="z-10"
      [media]="ppa.Media"
      [alt]="ppa.name"
      [placeholderIcon]="typeIconName"
      [tappable]="true"
      (photoTap)="openMorePpaDetails(ppa)"
    ></app-photo-strip>
    <ng-template #typeIcon>
      <div class="h-full w-full flex items-center justify-center bg-primary/10 text-primary" aria-hidden="true">
        <mat-icon class="!text-5xl !w-auto !h-auto">{{ typeIconName }}</mat-icon>
      </div>
    </ng-template>
  </div>

  <mat-card-content class="ppa-body flex flex-col gap-2 !pt-4">
    <div class="flex items-start gap-1">
      <h3 class="flex-1 min-w-0 m-0 text-lg font-semibold leading-snug text-primary">
        <button type="button" class="ppa-card-link text-left" (click)="openMorePpaDetails(ppa)">{{ ppa.name }}</button>
      </h3>
      <button
        *ngIf="callerService.isLoggedIn"
        type="button"
        class="relative z-10 !-mt-2"
        [@iconChangeAnimation]="toggleBookmarkIcon"
        (click)="bookmarkPost()"
        mat-icon-button
        color="primary"
        [attr.aria-label]="bookmarkState ? 'Remove from saved' : 'Save this PPA'"
        [attr.aria-pressed]="bookmarkState"
      >
        <mat-icon [fontIcon]="toggleBookmarkIcon"></mat-icon>
      </button>
      <button type="button" class="relative z-10 !-mt-2 !-mr-2" mat-icon-button [matMenuTriggerFor]="ppaMenu" aria-label="More actions">
        <mat-icon>more_vert</mat-icon>
      </button>
    </div>

    <div class="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
      <span *ngIf="ppa.type_of_ppa" class="rounded px-1.5 py-0.5 text-xs font-medium bg-primary/10 text-primary">{{ ppa.type_of_ppa }}</span>
      <span *ngIf="ppaLocation" class="text-text-secondary">{{ ppaLocation }}</span>
    </div>

    <div *ngIf="address" class="text-sm text-text-secondary">
      <p class="m-0 line-clamp-3">{{ address }}</p>
      <ng-container *ngTemplateOutlet="moreLocations"></ng-container>
    </div>

    <!-- What it's like to serve here -->
    <div class="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-primary">
      <span *ngIf="reviewCount > 0; else noReviews" class="inline-flex items-center gap-1">
        <mat-icon class="!text-base !w-auto !h-auto text-[#E5A100]" aria-hidden="true">star</mat-icon>
        <span *ngIf="rating !== null" class="font-semibold">{{ rating | number: '1.1-1' }}</span>
        <span class="text-text-secondary">· {{ reviewCount | shortenNumber }} review{{ reviewCount === 1 ? '' : 's' }}</span>
      </span>
      <ng-template #noReviews>
        <span class="text-text-secondary">No reviews yet</span>
        <ng-container *ngTemplateOutlet="firstReviewChip"></ng-container>
      </ng-template>
      <span *ngIf="corperCount > 0" class="inline-flex items-center gap-1">
        <mat-icon class="!text-base !w-auto !h-auto" aria-hidden="true">groups</mat-icon>
        {{ corperCount | shortenNumber }} corper{{ corperCount === 1 ? '' : 's' }} posted here
      </span>
    </div>

    <blockquote *ngIf="latestQuote" class="m-0 text-sm italic text-text-secondary line-clamp-2">“{{ latestQuote }}”</blockquote>

    <div class="mt-auto flex flex-wrap items-center justify-between gap-2 pt-2">
      <ng-container *ngTemplateOutlet="housesChip"></ng-container>
      <time class="ml-auto text-xs text-text-tertiary" [attr.datetime]="ppa.created_at">
        Listed {{ ppa.created_at | date: 'MMM y' }}
      </time>
    </div>
  </mat-card-content>
</mat-card>

<!-- Phones on /ppa: a compact row, so a screen shows about three PPAs. A thumbnail (the first photo,
     or the type's icon - no empty photo box) beside the name, what and where, and the numbers; then
     the full address, the latest review, and the houses-nearby and review chips. Save and the menu
     sit on the name's line, so they don't make the row taller. The same stretched link as the card. -->
<ng-template #row>
  <mat-card class="ppa-card ppa-row relative overflow-hidden">
    <div class="flex items-center gap-3 px-3 pt-3 pb-2">
      <div class="shrink-0 w-16 h-16 rounded-lg overflow-hidden flex items-center justify-center bg-primary/10 text-primary">
        <img *ngIf="!thumbnailFailed && rowThumbnail as thumb; else rowIcon" [src]="thumb.src" [attr.srcset]="thumb.srcset" sizes="64px"
          alt="" loading="lazy" class="w-full h-full object-cover" (error)="thumbnailFailed = true" />
        <ng-template #rowIcon><mat-icon aria-hidden="true">{{ typeIconName }}</mat-icon></ng-template>
      </div>

      <div class="flex-1 min-w-0">
        <div class="flex items-start gap-1">
          <!-- The clamp is on a span inside a block button: -webkit-line-clamp needs a block, and an inline-block button adds a baseline gap -->
          <h3 class="flex-1 min-w-0 m-0 text-base font-semibold leading-snug text-primary">
            <button type="button" class="ppa-card-link block text-left" (click)="openMorePpaDetails(ppa)"><span class="line-clamp-2">{{ ppa.name }}</span></button>
          </h3>
          <!-- 40px to see, 48px to tap (Material's touch target); the negative margins keep them out of the line's height -->
          <div class="relative z-10 shrink-0 flex -my-2 -mr-2">
            <button
              *ngIf="callerService.isLoggedIn"
              type="button"
              class="ppa-row-icon-button"
              [@iconChangeAnimation]="toggleBookmarkIcon"
              (click)="bookmarkPost()"
              mat-icon-button
              color="primary"
              [attr.aria-label]="bookmarkState ? 'Remove from saved' : 'Save this PPA'"
              [attr.aria-pressed]="bookmarkState"
            >
              <mat-icon [fontIcon]="toggleBookmarkIcon"></mat-icon>
            </button>
            <button type="button" class="ppa-row-icon-button" mat-icon-button [matMenuTriggerFor]="ppaMenu" aria-label="More actions">
              <mat-icon>more_vert</mat-icon>
            </button>
          </div>
        </div>
        <p class="m-0 text-sm text-text-secondary truncate">
          {{ ppa.type_of_ppa }}<ng-container *ngIf="ppa.type_of_ppa && ppaLocation"> · </ng-container>{{ ppaLocation }}
        </p>
        <p class="m-0 flex items-center gap-1 text-sm text-text-secondary truncate">
          <ng-container *ngIf="reviewCount > 0; else rowNoReviews">
            <mat-icon class="!text-base !w-auto !h-auto text-[#E5A100]" aria-hidden="true">star</mat-icon>
            <span *ngIf="rating !== null" class="font-semibold text-primary">{{ rating | number: '1.1-1' }}</span>
            <span>· {{ reviewCount | shortenNumber }} review{{ reviewCount === 1 ? '' : 's' }}</span>
          </ng-container>
          <ng-template #rowNoReviews><span>No reviews yet</span></ng-template>
          <span *ngIf="corperCount > 0">· {{ corperCount | shortenNumber }} corper{{ corperCount === 1 ? '' : 's' }}</span>
        </p>
      </div>
    </div>

    <div *ngIf="address || latestQuote || housesNearby > 0 || reviewCount === 0" class="flex flex-col gap-1.5 px-3 pb-3 text-sm">
      <!-- Where it is, in full: the street is often what tells two branches apart. Three lines at
           most, for the odd very long one - the details have it all. -->
      <div *ngIf="address" class="flex items-end gap-1 text-text-secondary">
        <mat-icon inline class="shrink-0 !text-base !w-auto !h-auto" aria-hidden="true">location_on</mat-icon>
        <div class="min-w-0">
          <p class="m-0 line-clamp-3">{{ address }}</p>
          <ng-container *ngTemplateOutlet="moreLocations"></ng-container>
        </div>
      </div>

      <!-- What corpers say -->
      <blockquote *ngIf="latestQuote" class="m-0 italic text-text-secondary truncate">“{{ latestQuote }}”</blockquote>

      <!-- What you can do from here, side by side; 16px apart if they wrap, so their tap areas don't overlap -->
      <div *ngIf="housesNearby > 0 || reviewCount === 0" class="flex flex-wrap gap-x-2 gap-y-4 pt-0.5">
        <ng-container *ngTemplateOutlet="housesChip"></ng-container>
        <ng-container *ngTemplateOutlet="firstReviewChip"></ng-container>
      </div>
    </div>
  </mat-card>
</ng-template>

<!-- Shared by both layouts, so the card and the row can't drift apart. Chips sit above the
     stretched link (z-10) and are 44px tall to tap (see .ppa-chip in the CSS). -->
<ng-template #housesChip>
  <!-- Below 360px "near here" goes, so the row's address still shows; the name keeps it -->
  <a
    *ngIf="housesNearby > 0"
    class="ppa-chip shrink-0"
    routerLink="/search"
    [queryParams]="housesQueryParams"
    [attr.aria-label]="(housesNearby | shortenNumber) + ' house' + (housesNearby === 1 ? '' : 's') + ' near here'"
  >
    <mat-icon aria-hidden="true">house</mat-icon>
    {{ housesNearby | shortenNumber }} house{{ housesNearby === 1 ? '' : 's' }}<span class="hidden min-[360px]:inline">near here</span>
  </a>
</ng-template>

<ng-template #firstReviewChip>
  <button *ngIf="reviewCount === 0" type="button" class="ppa-chip" (click)="leaveReview(ppa)" [disabled]="postingReview">
    <mat-icon aria-hidden="true">rate_review</mat-icon>
    {{ postingReview ? 'Posting review…' : 'Be the first to review' }}
  </button>
</ng-template>

<!-- Its own line, not the end of the address: a long address's last line is cut, and this with it -->
<ng-template #moreLocations>
  <p *ngIf="ppa.Locations && ppa.Locations.length > 1" class="m-0 text-xs">
    +{{ ppa.Locations.length - 1 }} more location{{ ppa.Locations.length === 2 ? '' : 's' }}
  </p>
</ng-template>

<!-- The ⋮ menu, for both layouts -->
<mat-menu #ppaMenu="matMenu">
  <button *ngIf="reviewCount > 0" type="button" mat-menu-item (click)="openMorePpaDetails(ppa)">
    <mat-icon>reviews</mat-icon>
    <span>See {{ reviewCount === 1 ? 'review' : 'reviews' }} ({{ reviewCount | shortenNumber }})</span>
  </button>
  <button type="button" mat-menu-item (click)="leaveReview(ppa)" [disabled]="postingReview">
    <mat-icon>rate_review</mat-icon>
    <span>{{ postingReview ? 'Posting review…' : 'Add review' }}</span>
  </button>
  <button type="button" mat-menu-item (click)="share()">
    <mat-icon>link</mat-icon>
    <span>Copy share link</span>
  </button>
  <button *ngIf="ppa.Locations && ppa.Locations.length > 1" type="button" mat-menu-item (click)="openMorePpaDetails(ppa)">
    <mat-icon>location_on</mat-icon>
    <span>View all {{ ppa.Locations.length }} locations</span>
  </button>
</mat-menu>
